import {
  RecipeCategory,
  RecipeType,
  RecipesSorting,
  type RecipeFilter,
  type RecipeSearchInput,
} from 'src/graphql/generated/operations';
import { normalizeRecipeCategory } from 'src/graphql/utils';

const MIN_SEARCH_LENGTH = 3;
const DEFAULT_SORT = RecipesSorting.MostRelevant;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Splits a comma-separated ID param, dropping empty and non-UUID values. */
export function parseIdListParam(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').map((id) => id.trim()).filter((id) => UUID_PATTERN.test(id));
}

export function parseRecipeCategory(value: string | null): RecipeCategory | undefined {
  const category = normalizeRecipeCategory(value);
  return category && Object.values(RecipeCategory).includes(category) ? category : undefined;
}

export function parseRecipeType(value: string | null): RecipeType | undefined {
  return value && Object.values(RecipeType).includes(value as RecipeType) ? value as RecipeType : undefined;
}

export function buildFilter(searchParams: URLSearchParams): RecipeFilter | undefined {
  const filter: RecipeFilter = {};
  const hasIngredients = parseIdListParam(searchParams.get('hasIngredients'));
  if (hasIngredients.length) {
    filter.hasIngredients = hasIngredients;
  }
  const category = parseRecipeCategory(searchParams.get('category'));
  if (category) {
    filter.category = category;
  }
  const type = parseRecipeType(searchParams.get('type'));
  if (type) {
    filter.type = type;
  }
  const cuisineTags = parseIdListParam(searchParams.get('cuisineTags'));
  if (cuisineTags.length) {
    filter.cuisineTags = cuisineTags;
  }
  const customTags = parseIdListParam(searchParams.get('customTags'));
  if (customTags.length) {
    filter.customTags = customTags;
  }
  const sort = searchParams.get('sort');
  if (sort && Object.values(RecipesSorting).includes(sort as RecipesSorting)) {
    filter.sort = sort as RecipesSorting;
  }

  return Object.keys(filter).length ? filter : undefined;
}

export function buildSearchInput(searchParams: URLSearchParams): RecipeSearchInput {
  const filter = buildFilter(searchParams) ?? {};
  const trimmedSearch = searchParams.get('nameSearch')?.trim() ?? '';

  return {
    category: filter.category,
    ingredientIDs: filter.hasIngredients,
    cuisineTagIDs: filter.cuisineTags,
    customTagIDs: filter.customTags,
    query: trimmedSearch.length >= MIN_SEARCH_LENGTH ? trimmedSearch : '',
    sort: filter.sort ?? DEFAULT_SORT,
    type: filter.type,
    limit: filter.limit,
    offset: filter.offset,
  };
}
